/**
 * src/lib/offline/enqueueOffline.js — shared offline-mutation gate for domain hooks.
 *
 * Each mutation handler calls maybeEnqueueOffline() right after applying its
 * optimistic state change. When the flag is on AND we're offline, the change is
 * durably queued (and the handler returns without hitting the network); when
 * online it's a no-op and the handler proceeds normally. Keeps the gate identical
 * across tasks / projects / CRM instead of copy-pasting the condition.
 */

import { getOnline } from './networkStatus';
import { enqueue } from './outbox';

/**
 * @param {{ offlineEnabled:boolean, domain:string, type:string, entity_id?:string, payload?:object }} op
 * @returns {Promise<{queued:boolean}>} queued=true means the caller should stop
 *   (the op is durably queued); queued=false means proceed with the online path.
 */
export async function maybeEnqueueOffline({ offlineEnabled, domain, type, entity_id = null, payload = {} }) {
  if (!offlineEnabled || getOnline()) return { queued: false };
  await enqueue({ domain, type, entity_id, payload });
  return { queued: true };
}

/** Stable client id for offline creates (idempotent replay). */
export function newOfflineId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // RFC4122 v4 fallback: section ids must stay valid UUIDs (server UUID_RX, TICKET-1051).
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (c ^ (Math.random() * 16) >> c / 4).toString(16));
}
